import {
  forwardRef,
  useMemo,
  useRef,
  type InputHTMLAttributes,
  type JSX,
  type Ref,
  type RefCallback,
} from 'react';
import clsx from 'clsx';
import { FormField, type FormFieldProps } from './FormField';

function mergeRefs<T>(...refs: Array<Ref<T>>): RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref !== null) {
        (ref as { current: T | null }).current = value;
      }
    });
  };
}

export type TextInputFieldProps = Omit<FormFieldProps, 'children' | 'onChange' | 'onBlur'> &
  Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'children'> & {
    inputClassName?: string;
  };

export const TextInputField = forwardRef<HTMLInputElement, TextInputFieldProps>(
  function TextInputField(
    {
      className,
      inputClassName,
      label,
      description,
      error,
      required,
      hint,
      labelSecondary,
      ...inputProps
    },
    forwardedRef,
  ): JSX.Element {
    const { id, ...restInputProps } = inputProps;
    const internalRef = useRef<HTMLInputElement | null>(null);
    const mergedRef = useMemo(() => mergeRefs(forwardedRef, internalRef), [forwardedRef]);

    return (
      <FormField
        id={id}
        label={label}
        description={description}
        error={error}
        required={required}
        hint={hint}
        labelSecondary={labelSecondary}
        className={className}
      >
        <input
          ref={mergedRef}
          required={required}
          className={clsx(
            'w-full rounded-xl border border-surface-subtle px-3 py-2.5 text-base focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 md:py-2 md:text-sm',
            inputClassName,
          )}
          id={id}
          {...restInputProps}
        />
      </FormField>
    );
  },
);
